/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Box,
  Tooltip,
  Badge,
} from '@mui/material';
import { CustomStack, CustomToggleSwitch } from 'modules/common/components';
import { CUSTOM_VALUE, KEYS, OTB_VALUE } from 'modules/dashboard/constants';
import CustomDatePicker from 'modules/dashboard/components/tab-container/inner-filter/components/custom-date-comparison';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import CollapseIcon from 'modules/common/components/collapse-icon';

/**
 * Vs comparison window component common for the dashboard component
 * @param {Boolean} customBooking - Custom booking selected state
 * @param {Boolean} alignmentToggle - Alignment toggle on/off state
 * @param {Function} handleToggleChange - Function to call when alignment toggle value changes
 * @param {Boolean} symmetricComparisonToggle - Symmetric comparison toggle on/off state
 * @param {Object} visual - PBI visuals for comparison window
 * @param {String} visualName - Display name for symmetric comparison visual
 * @param {*} dateComparison - Selected date comparison value
 * @param {Array} dateComparisonList - Date comparison list
 * @param {Function} handleComparisonChange - Function to call when date comparison value changes
 * @param {Function} handleSymmetricComparisonChange - Function to call symmetric comparison value changes
 * @param {Boolean} isCustomOptionAvailable - state to hide/show custom option
 * @param {Date} customDate - Custom date
 * @param {Date} startDate -  Start date
 * @param {Date} endDate - End date
 * @param {Date} targetDate - Target date
 * @param {*} reportType - reportType
 * @param {Function} handleCustomDates - Function to call when custom dates value changes
 * @returns
 */
const VsComparisonWindow = ({
  customBooking,
  alignmentToggle,
  handleToggleChange,
  symmetricComparisonToggle,
  visual,
  visualName,
  dateComparison,
  dateComparisonList,
  handleComparisonChange,
  handleSymmetricComparisonChange,
  isCustomOptionAvailable,
  customDate = null,
  reportType = null,
  startDate = null,
  endDate = null,
  targetDate = null,
  handleCustomDates = () => {},
  isDisplay,
  isDisplayOnClickAction = () => {},
  hideOTBAndCustom = false,
  isTimeComparisonDisabled = false,
  showStlp = true,
  hasLeap = false,
}) => (
  <CustomStack cssClassName={styles.innerFilter}>
    <FormControl>
      <Grid container justifyContent="space-between" flexDirection="row" alignItems="flex-start">
        <Grid item xs={4} paddingTop={1}>
          <FormLabel sx={{ fontSize: 13 }}>VS. Time</FormLabel>
        </Grid>
        {/* <Grid item xs={10} container flexDirection="row">
          <Grid
            container
            item
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={11}>
              <Typography fontSize={11}>{visualName}</Typography>
            </Grid>
            <Grid item xs={1}>
              <CollapseIcon isDisplay={isDisplay} onClick={isDisplayOnClickAction} tooltipText />
            </Grid>
            <Grid item xs={12}>
              {isDisplay && visual}
            </Grid>
          </Grid>
        </Grid> */}
      </Grid>
      <br />
      <CustomToggleSwitch
        label="Alignment"
        secondaryLabel="day-of-week"
        title="Align day-of-week between selected & comparison stay dates."
        onChange={(event) => handleToggleChange(event)}
        checked={alignmentToggle}
        disabled={isTimeComparisonDisabled}
        paddingBottom={3}
        className="alignment-toggle"
      />
      {showStlp && (
        <CustomToggleSwitch
          label="Same Time Last Period"
          title="Use comparable lead-time and booking window between selected & comparison stay dates."
          onChange={handleSymmetricComparisonChange}
          checked={symmetricComparisonToggle}
          disabled={isTimeComparisonDisabled}
          paddingBottom={3}
          className="sltp-toggle"
        />
      )}
      <Grid item xs={10} container flexDirection="row" paddingBottom={2}>
        <Grid container item flexDirection="row" alignItems="center" justifyContent="space-between">
          <Grid item xs={11}>
            <Typography fontSize={11}>{visualName}</Typography>
          </Grid>
          <Grid item xs={1} className='symmetric-comparison-window'>
            <CollapseIcon isDisplay={isDisplay} onClick={isDisplayOnClickAction} tooltipText />
          </Grid>
          <Grid item xs={12}>
            {isDisplay && visual}
          </Grid>
        </Grid>
      </Grid>
      {dateComparison && (
        <RadioGroup
          className='comparison-date-range'
          disabled
          value={dateComparison}
          onChange={(event) => handleComparisonChange(event)}
        >
          {isCustomOptionAvailable &&
            dateComparisonList?.map((item) => (
              <FormControlLabel
                key={item?.id}
                value={item?.value}
                control={<Radio />}
                disabled={
                  (item?.value === OTB_VALUE && !customBooking) ||
                  isTimeComparisonDisabled ||
                  (item?.value === OTB_VALUE && symmetricComparisonToggle && customBooking)
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {item?.label}
                    {dateComparison === item?.value && hasLeap && (
                      <Tooltip
                        title="Your comparison range includes a leap day. Totals displayed include the leap day values where applicable."
                        arrow
                      >
                        <Badge
                          color="error"
                          variant="dot"
                          invisible={
                            (item?.value === OTB_VALUE && !customBooking) ||
                            isTimeComparisonDisabled ||
                            (item?.value === OTB_VALUE &&
                              symmetricComparisonToggle &&
                              customBooking)
                          }
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          sx={{ pr: 1, mt: -2 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                }
                sx={{ my: -1 }}
              />
            ))}

          {!isCustomOptionAvailable &&
            dateComparisonList?.map((item) => {
              if (hideOTBAndCustom)
                return (
                  item.value !== CUSTOM_VALUE &&
                  item.value !== OTB_VALUE && (
                    <FormControlLabel
                      key={item?.id}
                      value={item?.value}
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          {item?.label}
                          {dateComparison === item?.value && hasLeap && (
                            <Tooltip
                              title="Your comparison range includes a leap day. Totals displayed include the leap day values where applicable."
                              arrow
                            >
                              <Badge
                                color="error"
                                variant="dot"
                                invisible={
                                  (item?.value === OTB_VALUE && !customBooking) ||
                                  isTimeComparisonDisabled ||
                                  (item?.value === OTB_VALUE &&
                                    symmetricComparisonToggle &&
                                    customBooking)
                                }
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'right',
                                }}
                                sx={{ pr: 1, mt: -2 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      }
                      disabled={
                        (item?.value === OTB_VALUE && !customBooking) ||
                        isTimeComparisonDisabled ||
                        (item?.value === OTB_VALUE && symmetricComparisonToggle && customBooking)
                      }
                      sx={{
                        my: -1,
                      }}
                    />
                  )
                );
              return (
                item.value !== CUSTOM_VALUE && (
                  <FormControlLabel
                    key={item?.id}
                    value={item?.value}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        {item?.label}
                        {dateComparison === item?.value && hasLeap && (
                          <Tooltip
                            title="Your comparison range includes a leap day. Totals displayed include the leap day values where applicable."
                            arrow
                          >
                            <Badge
                              color="error"
                              variant="dot"
                              invisible={
                                (item?.value === OTB_VALUE && !customBooking) ||
                                isTimeComparisonDisabled
                              }
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                              }}
                              sx={{ pr: 1, mt: -2 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    disabled={
                      (item?.value === OTB_VALUE && !customBooking) || isTimeComparisonDisabled
                    }
                    sx={{
                      my: -1,
                    }}
                  />
                )
              );
            })}
        </RadioGroup>
      )}
    </FormControl>
    {dateComparison && dateComparison === KEYS.CUSTOM && (
      <>
        <FormControl>
          <ToggleButtonGroup
            fullWidth
            color="primary"
            value={customDate}
            exclusive
            onChange={handleCustomDates}
          >
            <ToggleButton key="start" value={KEYS.START_DATE}>
              Start Date
            </ToggleButton>
            <ToggleButton key="end" value={KEYS.END_DATE}>
              End Date
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <Grid container direction="row" alignItems="center" justifyContent="center" paddingTop={0}>
          <CustomDatePicker
            reportType={reportType}
            targetDate={targetDate}
            customDate={customDate}
            startDate={startDate}
            endDate={endDate}
          />
        </Grid>
      </>
    )}
  </CustomStack>
);
//
export default VsComparisonWindow;
